// src/pages/JobDetail.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import WorkerSidebar from "../components/WorkerSidebar";

const API_WRK = process.env.REACT_APP_API_WRK;
// Derive API host (e.g., http://localhost:5000) from http://.../api/worker
const API_HOST = API_WRK?.replace(/\/api\/worker\/?$/, "") || window.location.origin;
const API_BASE = process.env.REACT_APP_API_BASE || API_HOST;
const API_JOB = `${API_BASE}/api/job`;

const JobDetail = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();

  const workerId = localStorage.getItem("workerId");

  const [job, setJob] = useState(null);
  const [worker, setWorker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!workerId) {
      navigate("/worker-login"); // adjust if different
      return;
    }

    const fetchData = async () => {
      try {
        const [jobRes, workerRes] = await Promise.all([
          axios.get(`${API_JOB}/${jobId}`),
          axios.get(`${API_WRK}/profile/${workerId}`),
        ]);
        setJob(jobRes.data);
        setWorker(workerRes.data);
      } catch (err) {
        console.error("❌ Error fetching job or worker:", err);
        setMessage("Failed to load job details.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [jobId, workerId, navigate]);

  const handleAcceptJob = async () => {
    try {
      await axios.post(`${API_WRK}/accept-job`, { jobId, workerId });
      setMessage("✅ Job accepted successfully!");
      setJob((prev) =>
        prev ? { ...prev, status: "assigned", assignedTo: workerId } : prev
      );
    } catch (err) {
      console.error("❌ Accept job error:", err);
      setMessage(err?.response?.data?.message || "Failed to accept job.");
    }
  };

  const handleCompleteJob = async () => {
    try {
      await axios.post(`${API_WRK}/complete-job`, { jobId, workerId });
      setMessage("✅ Job marked as completed!");
      setJob((prev) => (prev ? { ...prev, status: "completed" } : prev));
    } catch (err) {
      console.error("❌ Complete job error:", err);
      setMessage(err?.response?.data?.message || "Failed to complete job.");
    }
  };

  const handleCancelJob = async () => {
    const ok = window.confirm(
      "Are you sure you want to cancel this job?\nYour account will be suspended temporarily."
    );
    if (!ok) return;

    try {
      await axios.post(`${API_WRK}/cancel-job`, { jobId, workerId });
      setMessage("❌ Job cancelled. Your account has been temporarily suspended.");
      setJob((prev) => (prev ? { ...prev, status: "cancelled" } : prev));
    } catch (err) {
      console.error("❌ Cancel job error:", err);
      setMessage(err?.response?.data?.message || "Failed to cancel job.");
    }
  };

  const openChat = () => {
    // worker is sender, customer is receiver
    const customerId = job?.customerId?._id || job?.customerId;
    if (!customerId || !workerId || !jobId) return alert("Chat unavailable.");
    navigate(`/chat/${workerId}/${customerId}/${jobId}`);
  };

  if (loading) return <div className="p-6">Loading...</div>;
  if (!job || !worker) return <div className="p-6">❌ Job or profile not found.</div>;

  const assignedTo =
    String(job.assignedTo?._id || job.assignedTo || job.acceptedBy || "");
  const isAssignedToMe = assignedTo === String(workerId);

  return (
    <div className="flex min-h-screen bg-gray-50">
      <WorkerSidebar />
      <div className="ml-0 md:ml-60 p-6 w-full max-w-3xl text-gray-800">
        <h1 className="text-3xl font-bold mb-4 text-blue-700">{job.jobTitle}</h1>

        <p className="mb-2"><strong>Description:</strong> {job.description}</p>
        <p className="mb-2"><strong>Location:</strong> {job.location}</p>
        <p className="mb-2"><strong>Budget:</strong> ${job.budget}</p>
        <p className="mb-4">
          <strong>Scheduled:</strong>{" "}
          {job.scheduledAt ? new Date(job.scheduledAt).toLocaleString() : "ASAP"}
        </p>

        {worker.status !== "approved" ? (
          <div className="bg-yellow-100 text-yellow-800 p-4 rounded-lg mb-4">
            ⚠️ Your profile is under review. You cannot accept or complete jobs until approved.
          </div>
        ) : job.status === "assigned" && isAssignedToMe ? (
          <div className="flex gap-4 flex-wrap mb-4">
            <button
              onClick={handleCompleteJob}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded"
            >
              ✅ Mark as Completed
            </button>
            <button
              onClick={handleCancelJob}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded"
            >
              ❌ Cancel Job
            </button>
            <button
              onClick={openChat}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded"
            >
              💬 Message Customer
            </button>
          </div>
        ) : job.status === "assigned" ? (
          <div className="bg-gray-200 p-4 rounded mb-4 text-gray-700">
            This job has already been assigned to another worker.
          </div>
        ) : job.status === "completed" ? (
          <div className="bg-green-100 p-4 rounded text-green-700 mb-4">
            ✅ This job has been completed.
          </div>
        ) : job.status === "cancelled" ? (
          <div className="bg-red-100 p-4 rounded text-red-700 mb-4">
            ❌ This job has been cancelled.
          </div>
        ) : (
          <div className="flex gap-4 flex-wrap mb-4">
            <button
              onClick={handleAcceptJob}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded"
            >
              ✅ Accept Job
            </button>
            <button
              onClick={openChat}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded"
            >
              💬 Message Customer
            </button>
          </div>
        )}

        {message && (
          <p
            className={`mt-4 text-sm font-medium ${
              message.includes("✅") ? "text-green-600" : "text-red-600"
            }`}
          >
            {message}
          </p>
        )}
      </div>
    </div>
  );
};

export default JobDetail;
